import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { api } from '../api.js';
import { SettingsDrawer } from './SettingsDrawer.js';
import { t } from '../i18n.js';

export interface ShellContext {
  onOpenSettings: () => void;
}

const NAV = [
  { to: '/temperature', icon: '🌡️', key: 'nav.temperature' },
  { to: '/conso', icon: '⚡', key: 'nav.runtimes' },
  { to: '/video', icon: '📹', key: 'nav.video' },
  { to: '/infos', icon: 'ℹ️', key: 'nav.infos' },
  { to: '/souvenirs', icon: '📸', key: 'nav.memories' },
] as const;

export function AppShell() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [quotaLeft, setQuotaLeft] = useState<number | null>(null);

  // Fetch the quota lazily the first time Settings is opened.
  useEffect(() => {
    if (settingsOpen && quotaLeft === null) {
      void api.getQuota().then((q) => setQuotaLeft(q.remaining)).catch(() => {});
    }
  }, [settingsOpen, quotaLeft]);

  const shellContext = useMemo<ShellContext>(() => ({ onOpenSettings: () => setSettingsOpen(true) }), []);

  return (
    <div className="shell">
      <nav className="shell-nav">
        <span className="shell-brand">charzapp</span>
        <div className="shell-nav-links">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `shell-nav-link${isActive ? ' is-active' : ''}`}
            >
              <span className="shell-nav-icon" aria-hidden="true">{item.icon}</span>
              <span className="shell-nav-label">{t(item.key)}</span>
            </NavLink>
          ))}
        </div>
        <button
          className="shell-nav-link shell-nav-settings"
          onClick={() => setSettingsOpen(true)}
        >
          <span className="shell-nav-icon" aria-hidden="true">⚙</span>
          <span className="shell-nav-label">{t('nav.settings')}</span>
        </button>
      </nav>

      <main className="shell-main">
        <Outlet context={shellContext} />
      </main>

      {settingsOpen && (
        <SettingsDrawer onClose={() => setSettingsOpen(false)} quotaLeft={quotaLeft} />
      )}
    </div>
  );
}
